/**
 * DecisionFlow — API Client
 * Stage 8 Job A · A.2
 *
 * Thin fetch wrapper. Key responsibilities:
 *   - Always send credentials: "include" so the httpOnly df_session cookie
 *     is attached to every request (D7 primary auth).
 *   - Parse the canonical error envelope from api_contract §2.1.
 *   - On 401 from any protected endpoint: try one refresh, retry once,
 *     then propagate as AuthExpiredError so the UI can route to /login.
 *
 * All UI code interacts with the backend via apiGet / apiPost / apiPut /
 * apiDelete — never raw fetch().
 */

const BASE = ""; // Vite proxy routes /api/* → backend in dev; empty base keeps paths relative.

export class ApiError extends Error {
  constructor(status, code, message, detail, requestId) {
    super(message || `API error (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.requestId = requestId;
  }
}

export class AuthExpiredError extends ApiError {
  constructor(message, requestId) {
    super(401, "AUTH_TOKEN_EXPIRED", message, null, requestId);
    this.name = "AuthExpiredError";
  }
}

async function parseErrorResponse(res) {
  try {
    const body = await res.json();
    const envelope = body?.detail?.error || body?.error || null;
    if (envelope) {
      return {
        code: envelope.code || "UNKNOWN",
        message: envelope.message || "",
        detail: envelope.detail ?? null,
        requestId: envelope.request_id || null,
      };
    }
    return { code: "UNKNOWN", message: JSON.stringify(body), detail: null, requestId: null };
  } catch {
    return { code: "UNKNOWN", message: res.statusText || "", detail: null, requestId: null };
  }
}

let _refreshInFlight = null;

async function tryRefresh() {
  // Single-flight refresh: concurrent 401s share the same refresh call.
  if (!_refreshInFlight) {
    _refreshInFlight = fetch(`${BASE}/api/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        // Clear the in-flight marker after a small delay so any stacked
        // awaiters resolve against the same outcome.
        setTimeout(() => { _refreshInFlight = null; }, 50);
      });
  }
  return _refreshInFlight;
}

async function apiCall(method, path, body = null, { retryOn401 = true } = {}) {
  const opts = {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  };
  if (body !== null && body !== undefined) {
    opts.body = JSON.stringify(body);
  }

  let res = await fetch(`${BASE}${path}`, opts);

  if (res.status === 401 && retryOn401 && !path.startsWith("/api/v1/auth/")) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await fetch(`${BASE}${path}`, opts);
    }
  }

  if (!res.ok) {
    const err = await parseErrorResponse(res);
    if (res.status === 401 && retryOn401) {
      throw new AuthExpiredError(err.message, err.requestId);
    }
    throw new ApiError(res.status, err.code, err.message, err.detail, err.requestId);
  }

  if (res.status === 204) return null;

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }
  return res.text();
}

export const apiGet    = (path)          => apiCall("GET",    path);
export const apiPost   = (path, body)    => apiCall("POST",   path, body ?? {});
export const apiPatch  = (path, body)    => apiCall("PATCH",  path, body ?? {});
export const apiPut    = (path, body)    => apiCall("PUT",    path, body ?? {});
export const apiDelete = (path)          => apiCall("DELETE", path);

/**
 * Login — special-cased because it is the one endpoint that must NOT retry on 401.
 * A 401 on login is the user's problem (wrong password), not a token expiry.
 */
export async function apiLogin(email, password) {
  return apiCall("POST", "/api/v1/auth/login", { email, password }, { retryOn401: false });
}

/**
 * Logout — best-effort. Clears cookie server-side + client session state.
 */
export async function apiLogout() {
  try {
    await apiCall("POST", "/api/v1/auth/logout", {}, { retryOn401: false });
  } catch {
    // Ignore — user is signing out; network blip should not block redirect.
  }
}

/**
 * Get current user profile. Used by AuthContext on app mount.
 */
export async function apiMe() {
  return apiCall("GET", "/api/v1/auth/me", null, { retryOn401: false });
}
